export type VehicleType = '3W' | 'SCV' | 'Truck' | 'Tractor';
export type FuelType = 'Diesel' | 'Petrol' | 'CNG';

export interface ModelVariant {
  model: string;
  variant: string;
  range: number; // km
  batteryCapacity: number; // kWh
}

export interface AdvancedInput {
  shortName: VehicleType;
  fuelType: FuelType;
  fuelPrice: number;
  dailyDistance?: number; // km/day for 3W, SCV, HCV
  dailyHours?: number; // hours/day for Tractor
  modelVariant?: ModelVariant;
  // Optional overrides (if you have them)
  iceMileageKmPerUnit?: number; // km per liter/kg (or liters/hour for Tractor override not used)
  tractorLitersPerHour?: number; // default 2.5 L/h if not provided
  electricityCostPerKwh?: number; // default ₹10/kWh
  tractorEvCostPerHour?: number; // default ₹74.33/hour
}

const DAYS_IN_MONTH = 30;
const DAYS_IN_YEAR = 365;

const DEFAULT_ELECTRICITY_COST_PER_KWH = 10; // ₹/kWh
const DEFAULT_TRACTOR_EV_COST_PER_HOUR = 74.33; // ₹/hour

const DEFAULT_ICE_MILEAGE_BY_VEHICLE: Record<VehicleType, number> = {
  '3W': 27, // km per unit
  SCV: 12, // km per liter (Diesel)
  Truck: 3, // km per liter (Diesel)
  Tractor: 0, // handled via liters/hour
};

const DEFAULT_TRACTOR_LITERS_PER_HOUR = 2.5;

const DISTANCE_INTERVALS = [100, 500, 1000, 1500, 2000, 2500];
const TRACTOR_HOUR_INTERVALS = [1, 10, 50, 100];

const CUMULATIVE_PERIODS = [
  { label: '1 Month', days: 30 },
  { label: '1 Year', days: 365 },
  { label: '5 Year', days: 1825 },
  { label: '10 Year', days: 3650 },
];

function coalesce<T>(val: T | undefined, fallback: T): T {
  return typeof val === 'undefined' || val === null ? fallback : val;
}

function assertNumber(name: string, v: unknown): number {
  const n = Number(v);
  if (!isFinite(n)) throw new Error(`${name} must be a finite number`);
  return n;
}

/**
 * Daily ICE cost
 * - For 3W/SCV/HCV: (dailyDistance ÷ mileageKmPerUnit) × fuelPrice
 * - For Tractor: dailyHours × litersPerHour × fuelPrice
 */
export function calculateICEDailyCost(input: AdvancedInput): number {
  const fuelPrice = assertNumber('fuelPrice', input.fuelPrice);

  if (input.shortName === 'Tractor') {
    const hours = assertNumber('dailyHours', input.dailyHours ?? 0);
    const lph = coalesce(
      input.tractorLitersPerHour,
      DEFAULT_TRACTOR_LITERS_PER_HOUR
    );
    return hours * lph * fuelPrice;
  }

  const distance = assertNumber('dailyDistance', input.dailyDistance ?? 0);

  const mileage =
    input.iceMileageKmPerUnit ??
    DEFAULT_ICE_MILEAGE_BY_VEHICLE[input.shortName];
  if (mileage <= 0) return 0;

  return (distance / mileage) * fuelPrice;
}

/**
 * Daily EV cost
 * - For km-based (3W/SCV/HCV): dailyDistance × (batteryCapacity/range) × electricityCost
 * - Tractor: dailyHours × tractorEvCostPerHour
 */
export function calculateEVDailyCost(input: AdvancedInput): number {
  const elec = coalesce(
    input.electricityCostPerKwh,
    DEFAULT_ELECTRICITY_COST_PER_KWH
  );

  if (input.shortName === 'Tractor') {
    const hours = assertNumber('dailyHours', input.dailyHours ?? 0);
    const perHour = coalesce(
      input.tractorEvCostPerHour,
      DEFAULT_TRACTOR_EV_COST_PER_HOUR
    );
    return hours * perHour;
  }

  // km-based
  const distance = assertNumber('dailyDistance', input.dailyDistance ?? 0);
  const mv = input.modelVariant;
  if (!mv || mv.range <= 0) return 0;

  const kwhPerKm = mv.batteryCapacity / mv.range; // Efficiency: Battery ÷ Range
  return distance * kwhPerKm * elec;
}

export function calculateAdvancedSavings(input: AdvancedInput) {
  const dailyICE = calculateICEDailyCost(input);
  const dailyEV = calculateEVDailyCost(input);
  const dailySavings = dailyICE - dailyEV;

  return {
    dailyDieselCost: dailyICE,
    dailyElectricityCost: dailyEV,
    dailySavings,
    monthlySavings: dailySavings * DAYS_IN_MONTH,
    annualSavings: dailySavings * DAYS_IN_YEAR,
    tenYearSavings: dailySavings * (DAYS_IN_YEAR * 10),
  };
}

const BASIC = {
  DIESEL_MILEAGE: 8, // km/liter
  DIESEL_PRICE_PER_LITER: 90, // ₹
  ELECTRICITY_COST_PER_KWH: 10, // ₹
  VEHICLE_RANGE_KM: 160, // km
  EV_EFFICIENCY_KWH_PER_KM: 1.2, // kWh per km
};

export function calculateBasicSavings(dailyDistance: number) {
  const ice =
    (dailyDistance / BASIC.DIESEL_MILEAGE) * BASIC.DIESEL_PRICE_PER_LITER;
  const ev =
    dailyDistance *
    BASIC.EV_EFFICIENCY_KWH_PER_KM *
    BASIC.ELECTRICITY_COST_PER_KWH;
  const dailySavings = ice - ev;

  return {
    dailyDieselCost: ice,
    dailyElectricityCost: ev,
    dailySavings,
    monthlySavings: dailySavings * DAYS_IN_MONTH,
    annualSavings: dailySavings * DAYS_IN_YEAR,
    tenYearSavings: dailySavings * (DAYS_IN_YEAR * 10),
  };
}

export function calculateCosts(input: AdvancedInput) {
  const hasSpecs =
    !!input.modelVariant &&
    (input.shortName === 'Tractor' ||
      (input.modelVariant.range > 0 && input.modelVariant.batteryCapacity > 0));

  if (
    hasSpecs &&
    (input.shortName === 'Tractor'
      ? typeof input.dailyHours === 'number'
      : typeof input.dailyDistance === 'number')
  ) {
    return calculateAdvancedSavings(input);
  }

  const dd =
    input.shortName === 'Tractor' ? 0 : Number(input.dailyDistance ?? 0);
  return calculateBasicSavings(dd);
}

// ---------- Charts ----------
export type ChartMode = 'cost-comparison' | 'cumulative-savings';

export function getChartData(mode: ChartMode, base: AdvancedInput) {
  if (mode === 'cost-comparison') {
    if (base.shortName === 'Tractor') {
      // Hours-based intervals
      const labels = TRACTOR_HOUR_INTERVALS.map(
        (h) => `${h} ${h === 1 ? 'Hr' : 'Hrs'}`
      );
      const ice: number[] = [];
      const ev: number[] = [];

      for (const hours of TRACTOR_HOUR_INTERVALS) {
        const iceCost = calculateICEDailyCost({ ...base, dailyHours: hours });
        const evCost = calculateEVDailyCost({ ...base, dailyHours: hours });
        ice.push(iceCost);
        ev.push(evCost);
      }

      return {
        labels,
        datasets: [
          { label: 'ICE Cost', data: ice },
          { label: 'EV Cost', data: ev },
        ],
      };
    }

    // Distance-based intervals
    const labels = DISTANCE_INTERVALS.map((km) => `${km} km`);
    const ice: number[] = [];
    const ev: number[] = [];

    for (const km of DISTANCE_INTERVALS) {
      const iceCost = calculateICEDailyCost({ ...base, dailyDistance: km });
      const evCost = calculateEVDailyCost({ ...base, dailyDistance: km });
      ice.push(iceCost);
      ev.push(evCost);
    }

    return {
      labels,
      datasets: [
        { label: 'ICE Cost', data: ice },
        { label: 'EV Cost', data: ev },
      ],
    };
  }

  const labels = CUMULATIVE_PERIODS.map((p) => p.label);

  const { dailySavings } = calculateAdvancedSavings(base);
  const data = CUMULATIVE_PERIODS.map((p) => dailySavings * p.days);

  return {
    labels,
    datasets: [{ label: 'Cumulative Savings', data }],
  };
}
